import { Paintbrush } from "lucide-react";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";
import {
  UseCaseHero,
  UseCaseContext,
  UseCaseProblems,
  UseCaseSolution,
  UseCaseResults,
  UseCaseTestimonial,
  UseCaseCTA,
  RelatedUseCases,
} from "@/components/use-cases";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const ACCENT_COLOR = "#8B5CF6";

const problems = [
  "Marges tres serrees sur les prix au m2",
  "Retards de livraison peinture = journees perdues",
  "Dependance aux autres corps d'etat (le plaquiste en retard = il attend)",
  "Difficile de negocier des rallonges avec le client",
];

const solutions = [
  {
    title: "Rentabilite temps reel",
    description: "Meme sur les petits chantiers",
  },
  {
    title: "Simulation retards",
    description: "Impact financier si le chantier glisse",
  },
  {
    title: "Suivi consommation",
    description: "Litres de peinture utilises vs prevus",
  },
  {
    title: "Historique",
    description: "Donnees pour mieux estimer les prochains devis",
  },
];

const results = [
  {
    metric: "Marge moyenne",
    before: "5%",
    after: "11% (+6 pts)",
  },
  {
    metric: "Ajustements en cours de chantier",
    before: "Rares",
    after: "Systematiques",
  },
  {
    metric: "Devis refuses pour prix trop bas",
    before: "Frequents",
    after: "Reduits",
  },
];

const Peintre = () => {
  usePageMeta({
    title: "Logiciel gestion chantier peintre — B8ild",
    description: "Suivi de rentabilité pour peintres en bâtiment. Gérez vos chantiers de peinture et finition, calculez vos marges.",
    url: "https://b8ild.fr/use-cases/peintre",
  });

  return (
    <div className="min-h-screen bg-[#0A0A0F] overflow-x-hidden">
      <LandingHeader />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Cas d'usage", item: "https://b8ild.fr/use-cases" },
          { "@type": "ListItem", position: 3, name: "Peintre", item: "https://b8ild.fr/use-cases/peintre" },
        ],
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Logiciel gestion chantier peintre — B8ild",
        description: "Suivi de rentabilité pour peintres en bâtiment. Gérez vos chantiers de peinture et finition, calculez vos marges.",
        url: "https://b8ild.fr/use-cases/peintre",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
        inLanguage: "fr",
      }} />

      <UseCaseHero
        badge="Peintre"
        title="Karim transforme chaque m2"
        subtitle="en profit. Avec precision."
        description="Les peintres travaillent au m2 avec des marges serrees. B8ild suit vos couts en temps reel pour que chaque chantier reste rentable."
        icon={Paintbrush}
        accentColor={ACCENT_COLOR}
        personaInitials="KM"
        personaName="Karim"
        personaRole="Artisan peintre + 2 employes"
        personaCity="Bordeaux · CA ~220 000 EUR/an"
      />

      <UseCaseContext
        context="Karim est peintre depuis 10 ans. Avec ses 2 employes, il travaille principalement en sous-traitance pour des entreprises generales. Les prix au m2 sont serres (3-8% de marge), et le moindre derapage efface le benefice."
        accentColor={ACCENT_COLOR}
      />

      <UseCaseProblems
        problems={problems}
        accentColor={ACCENT_COLOR}
      />

      <UseCaseSolution
        solutions={solutions}
        accentColor={ACCENT_COLOR}
      />

      <UseCaseResults
        results={results}
        accentColor={ACCENT_COLOR}
        period="3 mois"
      />

      <UseCaseTestimonial
        quote="Sur un chantier de 200m2, j'ai vu que ma marge fondait a cause d'une sous-couche supplementaire. J'ai pu negocier un avenant avec le client, donnees a l'appui."
        name="Karim"
        role="Artisan peintre"
        city="Bordeaux"
        initials="KM"
        accentColor={ACCENT_COLOR}
      />

      {/* Spécificités métier */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Vos chantiers, on les connaît
            </h2>
            <p className="text-white/60 leading-relaxed">
              Peinture intérieure, ravalement façade, ITE (isolation thermique extérieure), enduit, finition — vous travaillez au m² avec des marges serrées. Une sous-couche de plus ou un retard de livraison et le chantier passe dans le rouge. B8ild calcule votre marge au m² en temps réel pour que vous sachiez exactement où vous en êtes avant la fin du chantier.
            </p>
          </div>
        </div>
      </section>

      {/* Cas concret */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Cas concret : 6 logements identiques et 3 000€ de marge perdue sur la préparation
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Karim a décroché un chantier de 6 logements T3 identiques dans un programme neuf. Devis : 4 200€ par logement, 25 200€ au total. Il a calculé 3 jours par logement : 1 jour de préparation (lessivage, enduit, ratissage), 2 jours de peinture (sous-couche + 2 couches). Soit 18 jours au total pour 2 peintres = 6 480€ de main d'œuvre. Matériaux : 8 400€. Marge prévisionnelle : 10 320€ (40,9%).
              </p>
              <p>
                Problème : les murs du promoteur sont en pire état que prévu. L'enduit de ratissage prend 1,5 jour au lieu de 0,5 par logement. 6 jours de travail supplémentaires × 2 peintres × 180€ = 2 160€ de surcoût main d'œuvre. Plus 600€ d'enduit supplémentaire. Marge réelle : 7 560€ (30%). Encore correcte, mais 3 000€ de moins que prévu.
              </p>
              <p>
                Le vrai piège : Karim n'a pas vu le problème sur les 2 premiers logements. Il pensait que "ça se rattraperait sur les suivants". Avec un suivi chantier par lot, il aurait détecté la dérive dès le logement n°1 et négocié un avenant avec le promoteur.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions métier */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions fréquentes — Peintres
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Comment estimer le temps de préparation des murs ?",
                  a: "La préparation représente 30 à 60% du temps total selon l'état des supports. Murs neufs (placo) : 20-30%. Rénovation légère : 40-50%. Rénovation lourde (décollage papier, reprise d'enduit) : 50-70%. Comptez toujours la préparation à part dans vos devis. C'est le poste qui dérape le plus souvent.",
                },
                {
                  q: "Quel rendement au m² pour un peintre ?",
                  a: "En conditions normales : 8 à 12m²/heure pour une application de peinture (rouleau). Avec sous-couche + 2 couches : 3 à 4m²/heure au total. La préparation (enduit, ponçage) : 2 à 5m²/heure selon l'état. Un logement T3 de 65m² (murs + plafonds : ~160m²) prend 40 à 55 heures de travail effectif.",
                },
                {
                  q: "Comment facturer un chantier de peinture pour un promoteur ?",
                  a: "Au forfait par logement (pas au m²) — ça simplifie la négociation. Prévoyez une réserve de 10% pour les imprévus (supports dégradés, retouches). Demandez un acompte de 30% au démarrage. Facturez par lot de 2-3 logements terminés. Ne commencez pas le lot suivant si le précédent n'est pas payé.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <UseCaseCTA accentColor={ACCENT_COLOR} />

      <RelatedUseCases currentSlug="peintre" />

      <LandingFooter />
    </div>
  );
};

export default Peintre;
